import React, { useEffect, useState, useRef } from "react";
import {
  FormLabel,
  InputAdornment,
  TextField,
  type TextFieldProps,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import {
  containsUnsafePayload,
  shouldBlockUnsafeSearchInput,
} from "@/helpers/sanitizeHelper";

const SEARCH_INPUT_FONT_SIZE = "0.75rem";

type MuiSearchInputProps = Omit<TextFieldProps, "onChange" | "value" | "sx"> & {
  value?: string | number | null;
  onChange?: (_value: string) => void;
  label?: React.ReactNode;
  debounceTime?: number;
  skipInitialChange?: boolean;
  sx?: Record<string, unknown>;
};

const MuiSearchInput = ({
  value,
  onChange,
  label,
  size = "small",
  placeholder = "Search..",
  debounceTime = 500,
  skipInitialChange = false,
  sx = {},
  ...rest
}: MuiSearchInputProps) => {
  const [searchValue, setSearchValue] = useState(() =>
    containsUnsafePayload(value) ? "" : String(value ?? "")
  );
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasMountedRef = useRef(false);
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (skipInitialChange && !hasMountedRef.current) {
      hasMountedRef.current = true;
      return undefined;
    }

    hasMountedRef.current = true;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(
      () => onChangeRef.current?.(String(searchValue)),
      debounceTime
    );
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [searchValue, skipInitialChange, debounceTime]);

  useEffect(() => {
    if (shouldBlockUnsafeSearchInput(value)) return;
    setSearchValue(String(value ?? ""));
  }, [value]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const nextValue = e.target.value;
    if (shouldBlockUnsafeSearchInput(nextValue)) return;

    setSearchValue(nextValue);
  };

  return (
    <div className="w-100">
      {label && <FormLabel className="m-0">{label}</FormLabel>}
      <TextField
        name="searchKey"
        id="searchKey"
        value={searchValue}
        onChange={handleChange}
        variant="outlined"
        placeholder={placeholder}
        fullWidth
        size={size}
        margin="none"
        className="search"
        color="primary"
        type="search"
        sx={{
          "& .MuiOutlinedInput-root": {
            minHeight: "40px",
            height: "40px",
            backgroundColor: "var(--neutral-white)",
          },
          "& .MuiInputBase-input": {
            fontSize: SEARCH_INPUT_FONT_SIZE,
          },
          "& input::placeholder": {
            fontSize: SEARCH_INPUT_FONT_SIZE,
          },
          ...sx,
        }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" fontSize="small" />
              </InputAdornment>
            ),
            autoComplete: "off",
          },
        }}
        {...rest}
      />
    </div>
  );
};

export default MuiSearchInput;
